import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  ViewChild,
  Output,
  ViewEncapsulation,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { EditorService } from 'src/app/core/_services/editor.service';
import ImageEditor from 'tui-image-editor';
import tuiImageEditor from 'tui-image-editor';

// import 'toast-ui-image-editor/dist/';
// import 'tui-image-editor/dist/svg/icon-b.svg';
// import 'tui-image-editor/dist/svg/icon-c.svg';
// import 'tui-image-editor/dist/svg/icon-d.svg';


enum editorEvents {
  addText = 'addText',
  mousedown = 'mousedown',
  objectActivated = 'objectActivated',
  objectMoved = 'objectMoved',
  objectScaled = 'objectScaled',
  redoStackChanged = 'redoStackChanged',
  textEditing = 'textEditing',
  undoStackChanged = 'undoStackChanged',
}
const includeUIOptions = {
  includeUI: {
    initMenu: null,
    menuBarPosition: 'right',
    // loadImage: {
    //   path: 'assets/images/tui.png', // URL of the initial image
    //   name: 'InitialImage'
    // },
  }
};
const editorDefaultOptions: tuiImageEditor.IOptions = {
  cssMaxWidth: 900,
  cssMaxHeight: 600
};

interface IImageEditor extends ImageEditor {
  off(eventName: string): void;
}

@Component({
  selector: 'tui-image-editor',
  template: `
    <div #imageEditor></div>
  `,
  styleUrls: [
    '../../../../node_modules/tui-color-picker/dist/tui-color-picker.css',
    '../../../../node_modules/tui-image-editor/dist/tui-image-editor.css'
  ],
  encapsulation: ViewEncapsulation.None
})
export class ToastUiImageEditorComponent implements AfterViewInit, OnDestroy, OnChanges {
  @Input() includeUI = true;
  @Input() options!: tuiImageEditor.IOptions;
  @Input() imageUrl: string;

  @Output() addText = new EventEmitter<any>();
  @Output() mousedown = new EventEmitter<any>();
  @Output() objectActivated = new EventEmitter<any>();
  @Output() objectMoved = new EventEmitter<any>();
  @Output() objectScaled = new EventEmitter<any>();
  @Output() redoStackChanged = new EventEmitter<number>();
  @Output() textEditing = new EventEmitter<void>();
  @Output() undoStackChanged = new EventEmitter<number>();

  @ViewChild('imageEditor', { static: true }) editorRef!: ElementRef;

  editorInstance!: ImageEditor;

  @Output() editorLoaded = new EventEmitter<boolean>();

  constructor(private editor: EditorService, private elRef: ElementRef) {

  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['imageUrl'].currentValue) {
      let options = editorDefaultOptions;
      if (this.includeUI) {
        if (this.imageUrl) {
          includeUIOptions.includeUI['loadImage'] = {
            path: this.imageUrl,
            name: this.imageUrl.split('/').pop()
          }
        }
        options = Object.assign(includeUIOptions, this.options);
      }
      this.editorInstance = new ImageEditor(
        this.editorRef.nativeElement,
        options
      );
      this.editor.setEditorInstance(this.editorInstance);
      this.addEventListeners();
      this.hideRangeElements();
      if (this.editorInstance) {
        this.editorLoaded.emit(true);
      }
      const logoElements = document.getElementsByClassName('tui-image-editor-header-logo');
      Array.from(logoElements).forEach((logo: Element) => {
        (logo as HTMLElement).style.display = 'none';
      });

      const headerButtons = this.elRef.nativeElement.querySelector('.tui-image-editor-header-buttons');
      if (headerButtons) {
        headerButtons.style.display = 'none'; // Hide the buttons
      }
    }
  }

  ngAfterViewInit() {
    // Run on window resize
    window.addEventListener('resize', this.hideRangeElements);
  }

  hideRangeElements = () => {
    const isTablet = /iPad|Android/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    if (window.innerWidth >= 1024 && !isTablet) { // ✅ Exclude tablets from disabling inputs
      const rangeElements = document.getElementsByClassName('tui-image-editor-range-value');
      Array.from(rangeElements).forEach((input: Element) => {
        (input as HTMLInputElement).setAttribute('disabled', 'true'); // ✅ Disable only on desktops
      });

      const rangeElementsSlider = document.getElementsByClassName('tui-image-editor-range');
      Array.from(rangeElementsSlider).forEach((slider: Element) => {
        (slider as HTMLElement).style.display = 'inline-block';
      });
    } else {
      const rangeElements = document.getElementsByClassName('tui-image-editor-range');
      Array.from(rangeElements).forEach((slider: Element) => {
        (slider as HTMLElement).style.display = 'none';
      });

      const rangeElementsValue = document.getElementsByClassName('tui-image-editor-range-value');

      Array.from(rangeElementsValue).forEach((input) => {
        const el = input as HTMLInputElement;
        el.removeAttribute('disabled'); // Ensure input is editable
        el.removeAttribute('readonly');
        el.style.display = 'inline';
        el.style.pointerEvents = 'auto';
        el.style.opacity = '1';
        el.style.userSelect = 'text';

        // 🔹 Detect if device is iOS (iPads/iPhones)
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
          (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

        if (isIOS) {
          // 🔹 Track last cursor position
          let lastCursorPos = el.value.length;

          // 🔹 Handle Backspace for **soft keyboard** users
          el.addEventListener('keydown', (event) => {
            if (event.key === 'Backspace') {
              event.preventDefault(); // Stop iOS from interfering
              setTimeout(() => {
                if (el.selectionStart !== null && el.selectionStart > 0) {
                  const newPos = el.selectionStart - 1;
                  el.value = el.value.slice(0, newPos) + el.value.slice(el.selectionEnd || el.value.length);
                  el.setSelectionRange(newPos, newPos); // Move cursor back
                  el.dispatchEvent(new Event('input')); // Trigger input update
                }
              }, 0);
            }
          });

          // 🔹 Handle Backspace for **external keyboard users**
          el.addEventListener('input', (event) => {
            event.stopPropagation();

            setTimeout(() => {
              if (el.selectionStart !== null) {
                lastCursorPos = el.selectionStart;
              }
            }, 0);
          });

          // 🔹 Force update when user exits the field
          el.addEventListener('blur', () => {
            el.setAttribute('value', el.value);
          });
        }
      });
    }
  };

  ngOnDestroy() {
    this.removeEventListeners();
    this.editorInstance.destroy();
  }

  private addEventListeners() {
    this.editorInstance.on(editorEvents.addText, (event: any) =>
      this.addText.emit(event)
    );
    this.editorInstance.on(editorEvents.mousedown, (event: any, originPointer: any) =>
      this.mousedown.emit({ event, originPointer })
    );
    this.editorInstance.on(editorEvents.objectActivated, (event: any) =>
      this.objectActivated.emit(event)
    );
    this.editorInstance.on(editorEvents.objectMoved, (event: any) =>
      this.objectMoved.emit(event)
    );
    this.editorInstance.on(editorEvents.objectScaled, (event: any) =>
      this.objectScaled.emit(event)
    );
    this.editorInstance.on(editorEvents.redoStackChanged, (event: any) =>
      this.redoStackChanged.emit(event)
    );
    this.editorInstance.on(editorEvents.textEditing, () =>
      this.textEditing.emit()
    );
    this.editorInstance.on(editorEvents.undoStackChanged, (event: any) =>
      this.undoStackChanged.emit(event)
    );
  }

  private removeEventListeners() {
    (<IImageEditor>this.editorInstance).off(editorEvents.addText);
    (<IImageEditor>this.editorInstance).off(editorEvents.mousedown);
    (<IImageEditor>this.editorInstance).off(editorEvents.objectActivated);
    (<IImageEditor>this.editorInstance).off(editorEvents.objectMoved);
    (<IImageEditor>this.editorInstance).off(editorEvents.objectScaled);
    (<IImageEditor>this.editorInstance).off(editorEvents.redoStackChanged);
    (<IImageEditor>this.editorInstance).off(editorEvents.textEditing);
    (<IImageEditor>this.editorInstance).off(editorEvents.undoStackChanged);
  }
}
